/**
 * Why Did You Render - Development Setup
 *
 * This file initializes @welldone-software/why-did-you-render for debugging
 * React component re-renders in development mode only.
 *
 * Usage:
 * 1. Set ENABLE_WDYR to true below (local only)
 * 2. Tag components with whyDidYouRender property to track them
 * 3. Check console for detailed re-render logs
 */
/* eslint-disable no-undef */

import React from 'react';
// WDYR patches React and every tracked hook app-wide and retains a lot of
// data per render, which can crash the browser tab (Chrome "Aw, Snap!") during
// long dev sessions. Keep it off and flip this flag locally only while
// debugging re-renders. Do not commit it as true.
const ENABLE_WDYR = false;

if (__DEV__ && ENABLE_WDYR) {
  try {
    const whyDidYouRender = require('@welldone-software/why-did-you-render');

    const hasReactInternals = Boolean(
      React &&
        React.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED &&
        React.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED
          .ReactCurrentOwner,
    );

    if (!hasReactInternals) {
      console.warn(
        '[WDYR] Skipping initialization: React internals unavailable (ReactCurrentOwner missing).',
      );
    } else {
      // Initialize WDYR with React
      whyDidYouRender(React, {
        // Track all pure components (components wrapped with React.memo)
        trackAllPureComponents: false, // Disabled to focus only on MainBanner

        // Track hooks (useState, useEffect, etc.)
        trackHooks: true,

        // Track extra hooks (custom hooks, redux hooks)
        trackExtraHooks: [
          // Redux hooks
          [require('react-redux'), 'useSelector', 'useDispatch'],
          // React Navigation hooks
          [
            require('@react-navigation/native'),
            'useNavigation',
            'useRoute',
            'useFocusEffect',
          ],
          // React Native hooks
          [require('react-native'), 'useColorScheme'],
        ],

        // Log only when values actually change
        logOnDifferentValues: true,

        // Collapse groups in console for better readability
        collapseGroups: true,

        // Include owner information
        includeOwner: false,

        // Hot reload support
        hotReloadBufferSize: 5,

        // Only track components that are explicitly tagged
        onlyLogs: true, // Only log tagged components
        include: [/MainBanner/], // Force-log just MainBanner for debugging

        // Custom name for the logger
        titleColor: 'green',

        // Disable in production builds
        // disabled: false,
      });

      console.log('🔍 Why Did You Render initialized successfully');
      console.log(
        '🔍 WDYR is tracking components with React version:',
        React.version,
      );
    }
  } catch (error) {
    console.warn('⚠️ Failed to initialize Why Did You Render:', error.message);
  }
}

// Helper function to tag components for tracking
export const tagComponent = (Component, options = {}) => {
  if (__DEV__ && Component) {
    Component.whyDidYouRender = {
      logOnDifferentValues: true,
      trackHooks: true,
      customName: options.name || Component.displayName || Component.name,
      ...options,
    };
  }
  return Component;
};

// Helper function to tag hooks for tracking
export const tagHook = (hook, name) => {
  if (__DEV__ && hook) {
    hook.whyDidYouRender = {
      logOnDifferentValues: true,
      customName: name || hook.name,
    };
  }
  return hook;
};

export default {
  tagComponent,
  tagHook,
};
